import { removePadding, addPadding } from "./helper";

//#region Signature generation

/**
 * 
 * @param {string} type e.g. HS256 (contains type & length)
 * @param {*} key provide the secret, can be a string or an object depending on the type
 * @param {string} message contains the header & payload (both base64 encoded), concatinated with a ".".
 * @returns {string} the signature
 */
const generateSignature = (type, key, message) => {
	if (type.indexOf("HS") != -1) {
		return hmac(key, message, type.substring(2, type.length));
	} else if (type.indexOf("RS") != -1 && key.length > 0) {
		return rsa(key, message, type.substring(2, type.length));
	}
}

/**
 * hmac
 * @param {string} key => contains the currently typed in secret 
 * @param {string} message => header & payload, concatinated with a . base 64 encoded.
 * @param {string} hashLength => (e.g. 256, 384 & 512)
 * @returns 
 */
const hmac = async (key, message, hashLength) => {
	const g = str => new Uint8Array([...str].map(c => c.charCodeAt(0))),
	k = g(key),
	m = g(message),
	c = await crypto.subtle.importKey('raw', k, { name: 'HMAC', hash: `SHA-${hashLength}` }, true, ['sign']),
	s = await crypto.subtle.sign('HMAC', c, m);

	return removePadding(window.btoa(String.fromCharCode(...new Uint8Array(s))));
}

/**
 * 
 * @param {object} key contains the private key
 * @param {string} message header & payload, concatinated with a dot & base 64 encoded.
 * @param {*} hashLength => e.g. 256, 384 & 512
 * @returns 
 */
const rsa = async (key, message, hashLength) => {
	const k = PEM2Binary(key);
	if (k == null) {
		return null;
	}
	
	const g = str => new Uint8Array([...str].map(c => c.charCodeAt(0))),
	m = g(message);
	c = await crypto.subtle.importKey('pkcs8', k, { name: "RSA-PSS", hash: { name: `SHA-${hashLength}` } }, false, ['sign']);
	s = await crypto.subtle.sign({ name: "RSA-PSS", saltLength: 128 }, c, m);
	return removePadding(window.btoa(String.fromCharCode(...new Uint8Array(s))));
}

//#endregion Signature generation

//#region Signature validation
/**
 * @param {string} type algorithm (e.g. HS256...)
 * @param {string} key containting the key that is used to verify (publicKey or secret)
 * @param {string} data (String for HMAC or Public Key for RSA)
 * @returns {boolean} true if the signature is valid, else false.
 */
const validateSignature = async (type, key, data) => {
	switch (type.substring(0, 2)) {
		case "HS":
			const check = await hmac(key, data.substring(0, data.lastIndexOf('.')), type.substring(2, type.length));
			return check === data.substring(data.lastIndexOf('.') + 1, data.length);
		case "RS":
			return validateRSA(key, data, type.substring(2, type.length));

	}
}

const validateRSA = async (key, data, hashLength) => {
	const keyAsBinary = PEM2Binary(key);
	if (keyAsBinary == null) {
		return null;
	}

	const publicKey = await crypto.subtle.importKey(
		"spki",
		keyAsBinary,
		{   //these are the algorithm options
			name: "RSA-PSS",
			hash: {name: `SHA-${hashLength}`},
		},
		false,
		["verify"]
	);

	const signature = window.atob(addPadding(data.substring(data.lastIndexOf('.') + 1, data.length)));

	const dataWithoutSignature = data.substring(0, data.lastIndexOf('.'));
	return crypto.subtle.verify(
		{ name: "RSA-PSS", saltLength: 128 },
		publicKey,
		new Uint8Array([...signature].map(c => c.charCodeAt(0))),
		new Uint8Array([...dataWithoutSignature].map(c => c.charCodeAt(0)))
	);
}

//#endregion Signature validation

//#region Helper functions
/**
 * TODO: document & refactor this
 * @param {*} pem 
 * @returns 
 */
const PEM2Binary = (pem) => {
	if (pem.length < 1 || pem === null) {
		return null;
	}
	// TODO: error handling needs to be improved, to improve stability when indicating if the signature is valid or invalid
	var encoded = '';
	var lines = pem.split('\n');
	for (var i = 0; i < lines.length; i++) {
		 if (lines[i].indexOf('-----') < 0) {
			  encoded += lines[i];
		 }
	}

	const base64Check = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{4})$/;
	if (!base64Check.test(encoded)) {
		return null;
	}

	var byteStr = window.atob(encoded);
	var bytes = new Uint8Array(byteStr.length);
	for (var i = 0; i < byteStr.length; i++) {
		 bytes[i] = byteStr.charCodeAt(i);
	}

	return bytes.buffer;
}

const getCurrentAlgorithm = () => {
	return document.getElementById('jwtAlgorithm')?.value || 'HS256';
}

const getCurrentSecret = () => {
	switch(getCurrentAlgorithm().substring(0, 2)) {
		case "HS": 
			return document.getElementById('jwtSecret')?.value || 'web-tools.app';
		case "RS":
			return document.getElementById('jwtPrivateKey')?.value || '';	
	}
}

const getValidationSubject = () => {
	switch(getCurrentAlgorithm().substring(0, 2)) {
		case "HS":
			return getCurrentSecret();
		case "RS":
			return document.getElementById('jwtPublicKey')?.value || '';
	}
}
//#endregion Helper functions

export { generateSignature, getCurrentAlgorithm, getCurrentSecret, validateSignature, getValidationSubject };